import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import { FormattedMessage, useIntl } from 'react-intl';
import SingleColumnTemplate from '../../../../../ui/layouts/SingleColumnTemplate.jsx';
import Card from '../../../../../ui/canon/Card';
import Button from '../../../../../ui/canon/Button';
import { formatCurrency } from '../../../utils/formatters';
import { getDealRoomBrandedName } from '../../../utils/locale';
import {
  BriefcaseIcon,
  DocumentTextIcon,
  InformationCircleIcon,
  ShieldCheckIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';

const DealsEmptyState = ({
  dealRoomName,
  mode, // 'seller' | 'seller_no_permission' | 'buyer' | 'no_motivation'
  onCreateDealRoom
}) => {
  const intl = useIntl();
  const t = (id, values) => intl.formatMessage({ id }, values);

  const title = (() => {
    if (mode === 'buyer') return t('succession_deals.empty_state.title_buyer', { dealRoomName });
    if (mode === 'no_motivation') return t('succession_deals.empty_state.title_no_motivation', { dealRoomName });
    if (mode === 'seller_no_permission') return t('succession_deals.empty_state.title_no_permission', { dealRoomName });
    return t('succession_deals.empty_state.title_seller', { dealRoomName });
  })();

  const description = (() => {
    if (mode === 'buyer') return t('succession_deals.empty_state.desc_buyer', { dealRoomName });
    if (mode === 'no_motivation') return t('succession_deals.empty_state.desc_no_motivation', { dealRoomName });
    if (mode === 'seller_no_permission') return t('succession_deals.empty_state.desc_no_permission', { dealRoomName });
    return t('succession_deals.empty_state.desc_seller', { dealRoomName });
  })();

  const showPrimaryCta = mode === 'seller';

  const features = (() => {
    if (mode === 'buyer') {
      return [
        { icon: BriefcaseIcon, title: t('succession_deals.empty_state.feature_buyer_1_title'), description: t('succession_deals.empty_state.feature_buyer_1_desc', { dealRoomName }) },
        { icon: DocumentTextIcon, title: t('succession_deals.empty_state.feature_buyer_2_title'), description: t('succession_deals.empty_state.feature_buyer_2_desc') },
        { icon: UserGroupIcon, title: t('succession_deals.empty_state.feature_buyer_3_title'), description: t('succession_deals.empty_state.feature_buyer_3_desc') }
      ];
    }
    if (mode === 'no_motivation') {
      return [
        { icon: ShieldCheckIcon, title: t('succession_deals.empty_state.feature_nomot_1_title'), description: t('succession_deals.empty_state.feature_nomot_1_desc') },
        { icon: DocumentTextIcon, title: t('succession_deals.empty_state.feature_nomot_2_title'), description: t('succession_deals.empty_state.feature_nomot_2_desc') },
        { icon: UserGroupIcon, title: t('succession_deals.empty_state.feature_nomot_3_title'), description: t('succession_deals.empty_state.feature_nomot_3_desc') }
      ];
    }
    if (mode === 'seller_no_permission') {
      return [
        { icon: ShieldCheckIcon, title: t('succession_deals.empty_state.feature_noperm_1_title'), description: t('succession_deals.empty_state.feature_noperm_1_desc') },
        { icon: DocumentTextIcon, title: t('succession_deals.empty_state.feature_noperm_2_title'), description: t('succession_deals.empty_state.feature_noperm_2_desc') },
        { icon: UserGroupIcon, title: t('succession_deals.empty_state.feature_noperm_3_title'), description: t('succession_deals.empty_state.feature_noperm_3_desc') }
      ];
    }
    return [
      { icon: ShieldCheckIcon, title: t('succession_deals.empty_state.feature_seller_1_title'), description: t('succession_deals.empty_state.feature_seller_1_desc') },
      { icon: DocumentTextIcon, title: t('succession_deals.empty_state.feature_seller_2_title'), description: t('succession_deals.empty_state.feature_seller_2_desc') },
      { icon: UserGroupIcon, title: t('succession_deals.empty_state.feature_seller_3_title'), description: t('succession_deals.empty_state.feature_seller_3_desc') }
    ];
  })();

  return (
    <div className="flex flex-col items-center py-6 px-4">
      {/* Main bordered card */}
      <Card variant="subtle" className="rounded-2xl p-8 md:p-10 max-w-2xl text-center">
        {/* Icon */}
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-succession-primary-10 mb-6 transition-theme duration-theme">
          <BriefcaseIcon className="w-10 h-10 text-succession-primary transition-theme duration-theme" />
        </div>

        {/* Title */}
        <h2 className="text-2xl md:text-3xl font-bold mb-4 text-succession-text-primary">
          {title}
        </h2>

        {/* Description */}
        <p className="text-succession-text-secondary text-lg mb-6 leading-relaxed">
          {description}
        </p>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 text-left">
          {features.map((f, idx) => (
            <Card
              key={idx}
              variant="plain"
              className="flex items-start gap-3 p-4 rounded-lg border border-succession-border"
            >
              <f.icon className="w-5 h-5 text-succession-primary flex-shrink-0 mt-0.5 transition-theme duration-theme" />
              <div>
                <p className="font-medium text-succession-text-primary text-sm">{f.title}</p>
                <p className="text-xs text-succession-text-secondary">{f.description}</p>
              </div>
            </Card>
          ))}
        </div>

        {/* CTA */}
        <div>
          {showPrimaryCta && (
            <Button type="button" size="lg" variant="primary" onClick={onCreateDealRoom} className="gap-2">
              <BriefcaseIcon className="w-5 h-5" />
              <FormattedMessage
                id="succession_deals.home.create_deal_room_button"
                values={{ dealRoomName }}
              />
              <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Button>
          )}

          {!showPrimaryCta && (
            <p className="text-sm text-succession-text-secondary">
              {mode === 'buyer'
                ? t('succession_deals.empty_state.cta_info_buyer', { dealRoomName })
                : mode === 'seller_no_permission'
                  ? t('succession_deals.empty_state.cta_info_seller_no_permission', { dealRoomName })
                : t('succession_deals.empty_state.cta_info_seller_default', { dealRoomName })}
            </p>
          )}
        </div>
      </Card>

      {/* Info Banner - Outside the bordered card */}
      <Card variant="outlined" className="bg-succession-primary-10 border border-succession-border rounded-lg p-4 mt-6 max-w-2xl w-full">
        <div className="flex items-start gap-3">
          <InformationCircleIcon className="w-5 h-5 text-succession-primary mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="font-medium text-succession-text-primary mb-1">
              {mode === 'buyer' ? t('succession_deals.empty_state.banner_title_buyer') : t('succession_deals.empty_state.banner_title_seller', { dealRoomName })}
            </p>
            <p className="text-succession-text-secondary">
              {mode === 'buyer'
                ? t('succession_deals.empty_state.banner_desc_buyer', { dealRoomName })
                : t('succession_deals.empty_state.banner_desc_seller')}
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

const GET_EVALUATING_DEALS = gql`
  query GetEvaluatingDeals {
    getCurrentUser {
      id
      motivation
      inventory_user {
        id
        can_match
        can_create_meeting_room
        deals {
          id
          deal_name
          headline
          sub_headline
          list_price
          deal_state
          closed
          deal_type
          matches_count
          number_of_invites
          number_of_proposals
          number_of_ndas
          number_of_negotiations
        }
        deal_interests {
          id
          workflow_state
          deal {
            id
            deal_name
            headline
            sub_headline
            list_price
            closed
            deal_state
            deal_type
          }
        }
      }
    }
  }
`;

const SuccessionDealsHome = ({ deals = [], initialEvaluatingDeals = [], currentUser, navigateTo, company }) => {
  const intl = useIntl();
  const { loading, error, data } = useQuery(GET_EVALUATING_DEALS);
  
  // Log any GraphQL errors for debugging
  if (error) {
    console.error('GraphQL error in SuccessionDealsHome:', error);
  }

  // Merge currentUser from props with GraphQL data so we always have the nested inventory_user flags
  const mergedUser = {
    ...(data?.getCurrentUser || {}),
    ...(currentUser || {}),
  };

  // Process deals the same way as the Ruby controller
  // Filter deal interests to match @evaluating in the home method of DealController
  const allDealInterests = data?.getCurrentUser?.inventory_user?.deal_interests || [];
  
  // Filter to exclude matching state and ensure deal state is published 
  const evaluatingDeals = allDealInterests.filter(di => 
    di.workflow_state !== 'matching' && 
    di.deal?.deal_state === 'published'
  );
  
  // Filter out only closed deals - buyers should see all their active deal interests including negotiating
  const filteredEvaluatingDeals = evaluatingDeals.filter(
    di => di?.deal && !di?.deal?.closed
  );

  // Determine if the user is a buyer, seller, or both (prefer merged user)
  const userMotivation = String(mergedUser?.motivation || '');
  const isBuyer = userMotivation.includes('buyer') || userMotivation.includes('growth');
  const isSeller = userMotivation.includes('seller') || userMotivation.includes('succession');
  const inventoryUser = mergedUser?.inventory_user || data?.getCurrentUser?.inventory_user;
  const hasDealRoom = Boolean(inventoryUser?.deals?.length > 0);
  const canCreateDealRoom = Boolean(inventoryUser?.can_create_meeting_room);
  const canMatch = Boolean(inventoryUser?.can_match);

  const shouldHideListPrice = (dealType) =>
    (dealType === 'continuity' && company?.features?.includes('hide_list_price_in_deal_room_wizard_continuity')) ||
    (dealType === 'succession' && company?.features?.includes('hide_list_price_in_deal_room_wizard_succession')) ||
    (dealType === 'sale' && company?.features?.includes('hide_list_price_in_deal_room_wizard_sale'));

  // Use company data from props
  const dealRoomBrandedName = getDealRoomBrandedName(company);
  const agentWord = intl.locale === 'fr' ? (company?.agent_word_fr || 'conseiller') : (company?.agent_word || 'Advisor');
  const agentWordPlural = agentWord.endsWith('s') ? agentWord : `${agentWord}s`;
  const agentWordPluralLower = agentWordPlural.toLowerCase();

  const navigateToWizard = () => {
    navigateTo('/succession/deals/wizard');
  };

  // Helper functions for SuccessionPageLayout
  const getPageTitle = () => {
    return intl.formatMessage({ id: 'succession_deals.home.page_title' }, { dealRoomName: dealRoomBrandedName });
  };

  const getPageSubtitle = () => {
    if (isSeller && isBuyer) {
      return intl.formatMessage(
        { id: 'succession_deals.home.subtitle_seller_buyer_with_agent_word', defaultMessage: 'Manage your deal rooms and evaluate opportunities from other {agentWordPlural}.' },
        { agentWordPlural: agentWordPluralLower }
      );
    } else if (isSeller) {
      return intl.formatMessage({ id: 'succession_deals.home.subtitle_seller' }, { dealRoomName: dealRoomBrandedName });
    } else if (isBuyer) {
      return intl.formatMessage({ id: 'succession_deals.home.subtitle_buyer' });
    }
    return intl.formatMessage({ id: 'succession_deals.home.subtitle_default' });
  };

  const getActionButtons = () => {
    const buttons = [];
    
    if (isSeller && canCreateDealRoom && canMatch && !hasDealRoom) {
      buttons.push({
        text: intl.formatMessage({ id: 'succession_deals.home.create_deal_room_button' }, { dealRoomName: dealRoomBrandedName }),
        onClick: navigateToWizard,
        icon: "calculate",
        variant: "primary"
      });
    }
    
    return buttons;
  };

  const headerActions = (() => {
    const items = getActionButtons();
    if (!items || items.length === 0) return null;
    return (
      <div className="flex gap-2">
        {items.map((btn, idx) => (
          <Button key={idx} onClick={btn.onClick} variant={btn.variant === 'primary' ? 'primary' : 'outline'}>
            {btn.text}
          </Button>
        ))}
      </div>
    );
  })();

  const isNoMotivation = userMotivation === 'no_motivation';
  // "Permission to create a deal room" means the same condition we already use for enabling the CTA.
  const sellerCanCreateDealRoom = Boolean(isSeller && canCreateDealRoom && canMatch);
  const sellerIsSuccession = Boolean(isSeller); // `isSeller` already includes motivation containing 'succession'

  const showEmptyState =
    !hasDealRoom &&
    deals.length === 0 &&
    filteredEvaluatingDeals.length === 0 &&
    (sellerIsSuccession || isBuyer || isNoMotivation);

  return (
    <SingleColumnTemplate
      title={getPageTitle()}
      subtitle={showEmptyState ? null : getPageSubtitle()}
      actions={showEmptyState ? null : headerActions}
      variant="wide"
    >

      {deals.length > 0 && (
        <Card className="p-6 mb-8">
          <h3 className="text-xl font-semibold mb-4">
            <FormattedMessage 
              id="succession_deals.home.your_deal_rooms_title" 
              values={{ dealRoomName: dealRoomBrandedName }}
            />
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-succession-border">
              <thead>
                <tr className="bg-succession-bg-secondary">
                  <th className="px-6 py-3 text-left text-xs font-medium text-succession-text-muted uppercase tracking-wider sticky left-0 z-20 bg-succession-bg-secondary">
                    {dealRoomBrandedName}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-succession-text-muted uppercase tracking-wider">
                    <FormattedMessage
                      id="succession_deals.home.table.matching"
                      defaultMessage="Matching"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-succession-text-muted uppercase tracking-wider">
                    <FormattedMessage
                      id="succession_deals.home.table.invited"
                      defaultMessage="Invited"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-succession-text-muted uppercase tracking-wider">
                    <FormattedMessage
                      id="succession_deals.home.table.expression_of_interest"
                      defaultMessage="Interest"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-succession-text-muted uppercase tracking-wider">
                    <FormattedMessage
                      id="succession_deals.home.table.ndas"
                      defaultMessage="NDAs"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-succession-text-muted uppercase tracking-wider">
                    <FormattedMessage
                      id="succession_deals.home.table.negotiation"
                      defaultMessage="Negotiation"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-succession-text-muted uppercase tracking-wider sticky right-0 z-20 bg-succession-bg-secondary">
                    <FormattedMessage
                      id="succession_deals.home.table.action"
                      defaultMessage="Action"
                    />
                  </th>
                </tr>
              </thead>
              <tbody className="bg-succession-bg-primary divide-y divide-succession-border">
                {deals.map(deal => {
                  // Use these metrics direct from GraphQL calculated fields
                  const matchingCount = deal.matches_count || 0;
                  const invitedCount = deal.number_of_invites || 0;
                  const reviewingProposalCount = deal.number_of_proposals || 0;
                  const ndaCount = deal.number_of_ndas || 0;
                  const negotiatingCount = deal.number_of_negotiations || 0;

                  return (
                    <tr key={deal.id}>
                      <td className="px-6 py-4 whitespace-normal min-w-0 sticky left-0 z-10 bg-succession-bg-primary">
                        <div className="text-sm font-medium text-succession-text-primary">#{deal.deal_name}</div>
                        <div
                          className="text-sm text-succession-text-secondary line-clamp-2 break-words max-w-[20rem] md:max-w-[24rem]"
                          title={deal.headline || ''}
                        >
                          {deal.headline}
                        </div>
                        <div className="mt-1">
                          {deal.closed ? (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-succession-error-bg text-succession-error">
                              <FormattedMessage id="succession_deals.home.status.closed" />
                            </span>
                          ) : !shouldHideListPrice(deal.deal_type) && (
                            deal.list_price ? (
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-succession-success-10 text-succession-success">
                                {formatCurrency(deal.list_price)}
                              </span>
                            ) : (
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-succession-bg-tertiary text-succession-text-muted">
                                <FormattedMessage id="succession_deals.home.status.tbd" />
                              </span>
                            )
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <Link
                          to={`/succession/deals/${deal.deal_name}/prospects/invite-advisors/evaluate-matches`}
                          className="text-xl font-semibold text-succession-text-primary hover:text-succession-primary"
                        >
                          {matchingCount}
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <Link
                          to={`/succession/deals/${deal.deal_name}/prospects/invite-advisors/invited`}
                          className="text-xl font-semibold text-succession-text-primary hover:text-succession-primary"
                        >
                          {invitedCount}
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <Link
                          to={`/succession/deals/${deal.deal_name}/prospects/review-interests/review-proposals`}
                          className="text-xl font-semibold text-succession-text-primary hover:text-succession-primary"
                        >
                          {reviewingProposalCount}
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <Link
                          to={`/succession/deals/${deal.deal_name}/prospects/execute-ndas/nda-out-for-review`}
                          className="text-xl font-semibold text-succession-text-primary hover:text-succession-primary"
                        >
                          {ndaCount}
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <Link
                          to={`/succession/deals/${deal.deal_name}/prospects/negotiate`}
                          className="text-xl font-semibold text-succession-text-primary hover:text-succession-primary"
                        >
                          {negotiatingCount}
                        </Link>
                      </td>
                      <td className="px-6 py-4 text-right whitespace-normal sticky right-0 z-10 bg-succession-bg-primary">
                        <Link
                          to={`/succession/deals/${deal.deal_name}/trailer`}
                          className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-succession text-white bg-succession-primary hover:bg-succession-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-succession-primary text-center max-w-[12rem] whitespace-normal"
                        >
                          <span className="line-clamp-2 break-words">
                            <FormattedMessage 
                              id="succession_deals.home.access_deal_room_button" 
                              values={{ dealRoomName: dealRoomBrandedName }}
                            />
                          </span>
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {filteredEvaluatingDeals.length > 0 && (
        <Card className="p-6 mb-8">
          <h3 className="text-xl font-semibold mb-4">
            <FormattedMessage 
              id="succession_deals.home.evaluating_deals_title" 
              values={{ dealRoomName: dealRoomBrandedName }}
            />
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-succession-border">
              <thead>
                <tr className="bg-succession-bg-secondary">
                  <th className="px-6 py-3 text-left text-xs font-medium text-succession-text-muted uppercase tracking-wider">
                    <FormattedMessage
                      id="succession_deals.home.table.deal"
                      defaultMessage="Deal"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-succession-text-muted uppercase tracking-wider">
                    <FormattedMessage
                      id="succession_deals.home.table.status"
                      defaultMessage="Status"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-succession-text-muted uppercase tracking-wider">
                    <FormattedMessage
                      id="succession_deals.home.table.actions"
                      defaultMessage="Actions"
                    />
                  </th>
                </tr>
              </thead>
              <tbody className="bg-succession-bg-primary divide-y divide-succession-border">
                {filteredEvaluatingDeals.map(dealInterest => {
                  const dotColor = (STATUS_STYLE_MAP[dealInterest.workflow_state] || DEFAULT_STATUS_STYLE).dot;
                  return (
                  <tr key={dealInterest.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <span className="rounded-full flex-shrink-0" style={{ width: '10px', height: '10px', backgroundColor: dotColor }} />
                        <div>
                      <div className="text-sm font-medium text-succession-text-primary">#{dealInterest.deal.deal_name}</div>
                      <div className="text-sm text-succession-text-secondary">{dealInterest.deal.headline}</div>
                      <div className="text-sm text-succession-text-secondary">{dealInterest.deal.sub_headline}</div>
                      {!shouldHideListPrice(dealInterest.deal.deal_type) && (
                        dealInterest.deal.list_price ? (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-succession-success-10 text-succession-success">
                            {formatCurrency(dealInterest.deal.list_price)}
                          </span>
                        ) : (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-succession-bg-tertiary text-succession-text-muted">
                            <FormattedMessage id="succession_deals.home.status.tbd" />
                          </span>
                        )
                      )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge workflow_state={dealInterest.workflow_state} />
                    </td>
                    <td className="px-6 py-4 whitespace-normal text-right">
                      <button
                        onClick={() => window.location.href = `/succession/deals/${dealInterest.deal.deal_name}/buyer_room`}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-succession text-white bg-succession-primary hover:bg-succession-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-succession-primary max-w-[12rem] text-center"
                      >
                        <span className="line-clamp-2 break-words">
                          <FormattedMessage id="succession_deals.home.access_buyer_room_button" />
                        </span>
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Empty state (matches MultiLineValuation visual language) */}
      {showEmptyState && (
        <DealsEmptyState
          dealRoomName={dealRoomBrandedName}
          mode={
            isNoMotivation
              ? 'no_motivation'
              : (isBuyer
                ? 'buyer'
                : (sellerCanCreateDealRoom ? 'seller' : 'seller_no_permission'))
          }
          onCreateDealRoom={navigateToWizard}
        />
      )}
    </SingleColumnTemplate>
  );
};

// Inline color config for status badges (avoids dynamic Tailwind class issues)
const STATUS_STYLE_MAP = {
  invited:                       { dot: '#3B82F6', bg: '#DBEAFE', text: '#1D4ED8' },
  reviewing_proposal:            { dot: '#F59E0B', bg: '#FEF3C7', text: '#B45309' },
  shortlisted:                   { dot: '#8B5CF6', bg: '#EDE9FE', text: '#6D28D9' },
  out_for_receiving_signature:   { dot: '#F97316', bg: '#FFEDD5', text: '#C2410C' },
  awaiting_disclosing_signature: { dot: '#F97316', bg: '#FFEDD5', text: '#C2410C' },
  negotiating:                   { dot: '#10B981', bg: '#D1FAE5', text: '#047857' },
  archived:                      { dot: '#EF4444', bg: '#FEE2E2', text: '#B91C1C' },
  declined:                      { dot: '#EF4444', bg: '#FEE2E2', text: '#B91C1C' },
  declined_by_buyer:             { dot: '#EF4444', bg: '#FEE2E2', text: '#B91C1C' },
};
const DEFAULT_STATUS_STYLE = { dot: '#9CA3AF', bg: '#F3F4F6', text: '#6B7280' };

const StatusBadge = ({ workflow_state }) => {
  const intl = useIntl();
  const labelKey = `succession_deals.home.workflow_states.${workflow_state}`;
  const fallback = (workflow_state || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const colors = STATUS_STYLE_MAP[workflow_state] || DEFAULT_STATUS_STYLE;

  return (
    <span className="inline-flex items-center text-xs font-semibold rounded-full" style={{ padding: '3px 10px', backgroundColor: colors.bg, color: colors.text }}>
      {intl.formatMessage({ id: labelKey, defaultMessage: fallback })}
    </span>
  );
};

export default SuccessionDealsHome; 